import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useLocale from 'hooks/useLocale';

import { setLoading } from 'stores/layoutSlice';
import { setMapsInstalled, setMapsLoading } from 'stores/mapsConfigSlice';

import AppLogger from 'utils/AppLogger';
import { store } from 'utils/configureStore';

const langCodes = {
	engValue: 'en',
	arabicValue: 'ar',
	trValue: 'tr'
};

let localizationConfig;

// Callback for the Google Maps API src
window.mapInstalledCallback = () => {
	store.dispatch({
		type: 'mapsConfig/completeMapsInstallation',
		payload: localizationConfig
	});
	store.dispatch({
		type: 'layout/setLoading',
		payload: false
	});
};

const MapLoader = () => {
	const { LanguageCode, Country = null } = useLocale();
	const dispatch = useDispatch();
	const {
		active,
		loading,
		installed,
		language: loadedLanguage,
		country: loadedCountry
	} = useSelector((state) => state.mapsConfig);

	const loadMapsScript = useCallback(() => {
		dispatch(setMapsLoading(true));
		dispatch(setLoading(true));

		// Destroy old API
		document.querySelectorAll('script[src^="https://maps.googleapis.com"]').forEach((script) => {
			script.remove();
		});
		if (window.google?.maps) delete window.google.maps;

		// Generate new Google Maps API script
		const mapsLoadScript = document.createElement('script');
		mapsLoadScript.defer = true;
		mapsLoadScript.async = true;

		const countryStr = Country ? `&region=${Country}` : '';
		const langStr = LanguageCode ? langCodes[LanguageCode] : 'en';
		mapsLoadScript.src = `https://maps.googleapis.com/maps/api/js?key=${
			import.meta.env.VITE_GOOGLE_MAPS_API_KEY
		}&loading=async&callback=mapInstalledCallback&language=${langStr}${countryStr}`;

		// Set error handler
		mapsLoadScript.onerror = (error) => {
			AppLogger.error('MapLoader: an error occurred while loading the Google Maps API script. ', error);
		};

		localizationConfig = {
			language: LanguageCode ?? null,
			country: Country ?? null
		};

		// Append the script to the head
		document.querySelector('head').appendChild(mapsLoadScript);
	}, [dispatch, LanguageCode, Country]);

	useEffect(() => {
		if (loading) return;

		if (installed && (loadedLanguage !== LanguageCode || loadedCountry !== Country)) {
			dispatch(setMapsInstalled(false));
			return;
		}

		if (active > 0 && !installed) {
			loadMapsScript();
		}
	}, [active, loading, installed, loadedLanguage, loadedCountry, LanguageCode, Country, loadMapsScript]);
};

export default MapLoader;
